import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/feature/SearchBar";
import ContentListItem from "@/components/feature/ContentListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar, { type FilterSection } from "@/components/feature/FilterSidebar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { contentItems, encyclopediaCategories } from "@/mocks/content";
import { searchContent } from "@/utils/search";

const PAGE_SIZE = 5;

export default function Encyclopedia() {
  const [params, setParams] = useSearchParams();
  const [showFilter, setShowFilter] = useState(false);

  const cat = params.get("cat") || "全部";
  const page = Number(params.get("page") || "1") || 1;

  const result = searchContent({
    type: "encyclopedia",
    category: cat,
    sort: "relevance",
    page,
    pageSize: PAGE_SIZE,
  });

  useDocumentMeta({
    title: "百科分类 - 百度一下",
    description: "百度一下百科频道，提供科技、交通、科学、历史等多领域词条内容浏览，帮助快速了解各类知识。",
    keywords: "百度一下百科,百科词条,知识查询,科普知识,词条解释",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "全部") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next);
  };

  const countCat = (c: string) =>
    contentItems.filter((i) => i.type === "encyclopedia" && (c === "全部" || i.category === c)).length;

  const sections: FilterSection[] = [
    {
      title: "词条分类",
      value: cat,
      options: encyclopediaCategories.map((c) => ({ label: c, value: c, count: countCat(c) })),
      onChange: (v) => updateParam("cat", v),
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-8 md:px-6 md:py-10">
            <div className="mx-auto max-w-6xl">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                百科
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                查询词条、了解知识，覆盖科技、科学、历史等多个领域。
              </p>
              <div className="mt-5">
                <SearchBar
                  placeholder="在百科内搜索词条关键词"
                  onSearch={(kw) => setParams({ q: kw, type: "encyclopedia" })}
                />
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
            <aside>
              <button
                type="button"
                onClick={() => setShowFilter((v) => !v)}
                className="mb-3 flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选分类
                <i className={`ri-arrow-down-s-line transition-transform ${showFilter ? "rotate-180" : ""}`} />
              </button>
              <div className="hidden lg:block">
                <FilterSidebar sections={sections} />
              </div>
              {showFilter && (
                <div className="lg:hidden">
                  <FilterSidebar sections={sections} />
                </div>
              )}

              <div className="mt-4 hidden rounded-lg border border-background-200 bg-background-100 p-4 lg:block">
                <h3 className="font-heading text-sm font-bold text-foreground-950">使用说明</h3>
                <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                  输入关键词缩小检索范围，点击词条可进入详情页查看解释。演示内容仅供展示。
                </p>
              </div>
            </aside>

            <div className="min-w-0">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  {cat === "全部" ? "全部词条" : `${cat}词条`}
                </h2>
                <span className="text-sm text-foreground-500">共 {result.total} 条</span>
              </div>

              {result.items.length > 0 ? (
                <>
                  <div className="flex flex-col gap-4">
                    {result.items.map((item) => (
                      <ContentListItem key={item.id} item={item} />
                    ))}
                  </div>
                  <Pagination current={result.page} totalPages={result.totalPages} onChange={goPage} />
                </>
              ) : (
                <EmptyState
                  icon="ri-book-2-line"
                  title="没有找到相关词条"
                  description="换个关键词，或查看全部百科词条。"
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={() => setParams({})}
                        className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                      >
                        查看全部词条
                      </button>
                      <Link
                        to="/"
                        className="rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        返回首页
                      </Link>
                    </>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}